/**
 * Skills Strategy - Queue-Driven Version
 * 
 * New flow: Get pending queue items for skills topic -> Loop through each item -> Show confirmation widget for each
 * Fully integrated with Central Discovery Queue
 */

import { runAIMatches } from '../api';
import { StrategyPhase } from './baseStrategy';
import centralDiscoveryQueue from '../centralDiscoveryQueue';
import { API_CONFIG } from '../../config/constants';
import { 
  createSkillsQueueItem, 
  createCompanyStageQueueItem, 
  createLocationQueueItem, 
  createExperienceQueueItem,
  createQueueItem,
  DISCOVERY_TEMPLATE_TYPES 
} from '../discoveryQueueSchema';
import { handleUnclearInput } from './clarifyingQuestions';

// Clean confirmation message for session restoration
export const restore_message = "Got it! Just to confirm — you're looking for candidates with the following skills, right? 👇";

/**
 * Extract skills from requirements for display in confirmation widget
 * @param {Array} requirements - The ICP requirements
 * @param {Object} queries - Queries object for looking up query IDs
 * @returns {Array} Array of skill strings
 */
const extractSkillsFromRequirements = (requirements, queries = {}) => {
  const skills = [];
  
  requirements.forEach(req => {
    if (req.section_id === "skills_mh" || req.section_id === "skills_gh") {
      if (req.bool_str) {
        // Simple parsing - split by common delimiters and clean up
        const skillsText = req.bool_str
          .replace(/\(|\)|AND|OR|\"|'/g, ' ') // Remove parentheses, AND, OR, quotes
          .split(/[,\s]+/) // Split by commas and spaces
          .filter(skill => skill.trim().length > 2) // Filter out short strings
          .map(skill => skill.trim())
          .filter(skill => skill.length > 0);
        // Process each skill - check if it's a query ID or regular string
        skillsText.forEach(skill => {
          if (queries && queries[skill]) {
            // It's a query ID - use the name from queries
            skills.push(queries[skill].name);
          } else {
            // It's a regular string - use as is
            skills.push(skill);
          }
        });
      }
    }
  });
  
  // Remove duplicates and return unique skills, limit to 6 for display
  return [...new Set(skills)].slice(0, 6);
};

/**
 * Extract findem magic attributes from requirements using queries lookup
 * @param {Array} requirements - The ICP requirements
 * @param {Object} queries - The queries object for looking up attribute names
 * @param {Array} baseSkills - Base skills extracted from requirements
 * @returns {Array} Expanded skills array including findem magic attributes
 */
const extractFindemMagicAttributes = (requirements, queries, baseSkills) => {
  let expandedSkills = [...baseSkills];
  
  // Find Findem Magic requirements
  const findemMagicReqs = requirements.filter(req => req.section_name === "Findem Magic");
  
  if (findemMagicReqs.length > 0 && queries) {
    console.log(`Found ${findemMagicReqs.length} Findem Magic requirements`);
    
    const findemMagicNames = [];
    
    findemMagicReqs.forEach(req => {
      if (req.bool_str) {
        // Parse the bool_str to extract hex strings (similar to skills parsing)
        const hexStrings = req.bool_str
          .replace(/\(|\)|AND|OR|\"|'/g, ' ') // Remove parentheses, AND, OR, quotes
          .split(/[,\s]+/) // Split by commas and spaces
          .filter(str => str.trim().length > 0)
          .map(str => str.trim());
        
        // For each hex string, look up the name in queries
        hexStrings.forEach(hexStr => {
          if (queries[hexStr].categories.indexOf("Verified Skills") >= 0) {
            if (queries[hexStr] && queries[hexStr].name) {
              findemMagicNames.push(queries[hexStr].name + " (Nice to Have)");
            }
          }
        });
      }
    });
    
    if (findemMagicNames.length > 0) {
      console.log('Found Findem Magic attribute names:', findemMagicNames);
      
      // Add findem magic attribute names to expanded skills (limit to top 3 to avoid overwhelming)
      const topFindemMagicSkills = [...new Set(findemMagicNames)] // Remove duplicates
        .slice(0, 3)
        .filter(name => name && name.trim().length > 0);
      
      expandedSkills.push(...topFindemMagicSkills);
      
      // Remove duplicates and limit total to 8 skills for optimal display
      expandedSkills = [...new Set(expandedSkills)].slice(0, 8);
      
      console.log('Expanded skills with Findem Magic:', expandedSkills);
    }
  }
  
  return expandedSkills;
};


/**
 * Infer topic from section name
 * @param {string} sectionName - The section name
 * @returns {string} Topic name
 */
const inferTopicFromSectionName = (sectionName) => {
  const sectionNameLower = sectionName.toLowerCase();
  
  // Skills and Findem Magic only
  if (sectionNameLower.includes('skill') || sectionName === 'Findem Magic') {
    return 'skills';
  }
  
  // Return null for non-skill sections - they won't be processed
  return null;
};

/**
 * Create queue item from individual ICP requirement
 * Creates the flat structure expected by the UI components
 * @param {Object} requirement - Single ICP requirement object (has section_id, section_name, bool_str, section_order, section_type)
 * @param {Object} queries - Queries object for lookups
 * @returns {Object|null} Queue item with flat structure or null
 */
const createQueueItemFromRequirement = async (requirement, queries = {}) => {
  const { section_name, section_type, bool_str } = requirement;
  
  if (!section_name) {
    console.log('⚠️ No section_name, skipping requirement');
    return null;
  }
  
  const topic = inferTopicFromSectionName(section_name);
  
  // Only process skills and Findem Magic - return null for others
  if (!topic) {
    return null;
  }
  
  // Generate unique queue ID
  const queueId = `queue_${topic}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // Create the flat queue item structure expected by the UI
  const queueItem = {
    // Core fields expected by UI
    queue_id: queueId,
    section_id: requirement.section_id || `${topic}_${Date.now()}`,
    section_name: requirement.section_name,
    section_type: requirement.section_type || 'Must Have',
    section_order: requirement.section_order || 0,
    bool_str: requirement.bool_str || '',
    topic: topic,
    disco_done: false,
    
    // Additional metadata for template system
    template_type: DISCOVERY_TEMPLATE_TYPES.SINGLE_ROW_SELECTOR,
    ai_intro_message: `Let's configure the requirements for ${section_name}.`,
    ux_config: {
      title: `Please confirm your requirements for ${section_name}:`,
      confirm_button_text: 'Confirm Selection'
    },
    template_props: {
      items: [
        { label: "Yes, this is important", value: "yes" },
        { label: "No, skip this", value: "no" }
      ],
      selectedItems: []
    },
    
    // Timestamps
    created_at: Date.now(),
    updated_at: Date.now()
  };
  
  const sectionNameLower = section_name.toLowerCase();

  // Configure template-specific fields based on requirement type
  if (sectionNameLower.includes('skill')) {
    // Skills requirements
    queueItem.template_type = DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR;
    queueItem.ai_intro_message = `Based on your input, I’ve identified some additional skills and attributes that could strengthen your search (see below 👇). Feel free to pick the ones that are most relevant — would you like me to include them?`;    
    queueItem.ux_config = {
      title: `t`,
      confirm_button_text: 'Confirm Skills',
      ro_footer: false,
      allow_empty_selection: false // Skills are required, but don't pre-select all
    };
    const skillItems = extractSkillsFromRequirements([requirement], queries).map(skill => ({
      label: skill,
      value: skill.toLowerCase().replace(/\s+/g, '_')
    }));
    
    queueItem.template_props = {
      sections: [
        {
          title: "Confirm Skills",
          items: skillItems
        }
      ],
      selectedItems: [] // Start with no skills pre-selected - user must select what they want
    };
  } else if (section_name === 'Findem Magic') {
    // Keep Findem Magic with original logic
    console.log('🎯 Creating Findem Magic queue item');
    queueItem.template_type = DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR;
    queueItem.ai_intro_message = `Based on your search, I’ve spotted some relevant attributes worth considering (see below 👇). Want me to include them?`;
    queueItem.ux_config = {
      title: `t`,
      allow_empty_selection: true,
      confirm_button_text: 'Confirm',


      // ro_footer defaults to true (read-only)
    };

    // Parse bool_str to extract query IDs
    const parseBoolStr = async (boolStr) => {
      if (!boolStr) return [];
      
      // Check if queries is empty and fetch if needed
      if (!queries || Object.keys(queries).length === 0) {
        try {
          const { fetchQueries } = await import('../../utils/api');
          queries = await fetchQueries();
        } catch (error) {
          console.error('Failed to fetch queries:', error);
        }
      }
      
      // Extract content from parentheses and split by OR/AND
      const parenthesesMatch = boolStr.match(/\(+([^)]+)\)+/);
      const elementsStr = parenthesesMatch ? parenthesesMatch[1] : boolStr;
      const elements = elementsStr.split(/\s+(OR|AND)\s+/);
      const values = elements.filter(element => element !== 'OR' && element !== 'AND');
      
      return values.map(quid => {
        const trimmedQuid = quid.trim();
        // Check if queries[quid] exists
        if (queries && queries[trimmedQuid]) {
          return {
            label: queries[trimmedQuid].name,
            value: trimmedQuid
          };
        } else {
          return {
            label: trimmedQuid,
            value: trimmedQuid
          };
        }
      });
    };

    // Make the items parsing async
    const items = await parseBoolStr(requirement.bool_str);
    
    // Don't add queue item if no items were parsed
    if (!items || items.length === 0) {
      return null;
    }
    
    queueItem.template_props = {
      sections: [
        {
          title: "Confirm",
          items: items
        }
      ],
      selectedItems: items.map(item => item.value) // Pre-select all Findem Magic attributes
    };  
  } 
  return queueItem;
};

/**
 * Create additional skill-related queue items from Findem Magic queries based on taxonomy
 * @param {Object} queries - All available queries
 * @param {Object} roleTypeConfig - Role type configuration with skillTaxonomyConfig
 * @returns {Array} Array of queue items for skill-related categories
 */
const createSkillTaxonomyQueueItems = (queries, roleTypeConfig = null) => {
  console.log('🎯 Creating skill taxonomy queue items from Findem Magic queries');
  
  // Check if taxonomy queues are enabled for this role type
  if (!roleTypeConfig || !roleTypeConfig.advancedFeatures || !roleTypeConfig.advancedFeatures.skills || !roleTypeConfig.advancedFeatures.skills.taxonomyConfig || !roleTypeConfig.advancedFeatures.skills.taxonomyConfig.enableTaxonomyQueues) {
    console.log('🎯 Taxonomy queues disabled for this role type, skipping');
    return [];
  }
  
  const enabledCategories = roleTypeConfig.advancedFeatures.skills.taxonomyConfig.categories || [];
  console.log('🎯 Enabled categories for this role type:', enabledCategories);
  
  const queueItems = [];
  let queueCounter = 1;

  // Use skill categories from advanced config if available, otherwise fallback to hardcoded
  const skillCategories = roleTypeConfig.advancedConfig?.skills?.taxonomy?.categories || {
    'Research Experience': {
      section_name: 'Publication and Cited Pubs Experience',
      title: 'Publication and Cited Pubs Experience',
      subtitle: 'Publication and cited publications experience distribution'
    },
    // 'Software Tools': {
    //   section_name: 'Tool Experience', 
    //   title: 'Tool Experience',
    //   subtitle: 'Tool experience distribution'
    // },
    'Technology': {
      section_name: 'Skills',
      title: 'Skills', 
      subtitle: 'Technology skills distribution'
    },
    'Software Development': {
      section_name: 'Verified Software Dev Experience',
      title: 'Verified Software Dev Experience',
      subtitle: 'Verified software development experience distribution'
    }
  };
  
  // Group queries by taxonomy categories
  const categoryQueries = {
    'Research Experience': [],
    // 'Software Tools': [],
    'Technology': [],
    'Software Development': []
  };
  
  // Process each query to categorize by taxonomy
  Object.entries(queries).forEach(([queryId, queryData]) => {
    if (queryData.name.toLowerCase().indexOf('companies') >= 0) return;
    if (!queryData.macro_taxonomy) return;
    queryData.macro_taxonomy.forEach(taxonomy => {
      // Check for null taxonomy items
      if (!taxonomy || typeof taxonomy !== 'string') return;
      
      // Check for each enabled skill category in taxonomy
      enabledCategories.forEach(category => {
        if (taxonomy.includes(category) && skillCategories[category]) {
          categoryQueries[category].push({ id: queryId, name: queryData.name });
          console.log(`🎯 Added to ${category}: ${queryData.name}`);
        }
      });
    });
  });

  // Create queue items for each category that has queries
  Object.entries(categoryQueries).forEach(([categoryName, queries]) => {
    if (queries.length === 0) return;
    
    const categoryConfig = skillCategories[categoryName];
    const boolStr = `((${queries.map(q => q.id).join(' OR ')}))`;
    const queueItem = {
      queue_id: `skills_fmmagic_${categoryName.toLowerCase().replace(/\s+/g, '_')}_${Date.now() + queueCounter++}`,
      section_id: 'fmmagic',
      section_name: categoryConfig.section_name,
      section_type: 'Must Have',
      section_order: queueCounter,
      topic: 'skills',
      bool_str: boolStr,
      disco_done: false,
      template_type: DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART,
      ai_intro_message: `I've analyzed your search criteria and found relevant ${categoryConfig.title.toLowerCase()}. Which ones would you like to target?`,
      ux_config: {
        title: '',
        chart_title: categoryConfig.title,
        chart_subtitle: categoryConfig.subtitle,
        confirm_button_text: 'Select at Least One Option',
        allow_empty_selection: true,
        hide_zero_percentage: true,
        allowEmptySelection: true
      },
      template_props: {
        data: queries.map(q => ({
          name: q.name,
          percentage: 0,
          queryId: q.id
        })),
        selectedItems: []
      },
      created_at: Date.now(),
      updated_at: Date.now()
    };
    
    queueItems.push(queueItem);
    console.log(`🎯 Created ${categoryConfig.title} queue item with ${queries.length} options`);
  });
  
  return queueItems;
};

/**
 * Main skills strategy function
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
const skillsStrategy = async (decision, messages, setMessages) => {
  console.log('🚨 SKILLS STRATEGY CALLED - TOP OF FUNCTION');
  console.log('🎯 SKILLS STRATEGY - Processing skills topic');
  console.log('🎯 SKILLS STRATEGY - decision object:', {
    sessionId: decision.sessionId,
    currentSessionId: decision.currentSessionId,
    userId: decision.userId,
    isJdUrl: decision.isJdUrl,
    getAllRequirements: !!decision.getAllRequirements,
    allDecisionProps: Object.keys(decision)
  });
  // Remove early return for empty messages - strategy should process even when messages is empty

  try {
    // Step 1: Run AI matches to get requirements
    let aiMatchesResult;
    const existingReqs = decision.getAllRequirements ? decision.getAllRequirements() : [];
    
    if (decision.isJdUrl) {
      // Use existing requirements from JD URL
      aiMatchesResult = {
        search_gen_response: {
          icp_requirements: existingReqs
        }
      };
    } else {
      // Check if we have any user messages - if not, this is topic initialization
      console.log('🔍 DEBUGGING: Raw messages passed to strategy:', messages);
      const userMessages = messages ? messages.filter(msg => msg.role === 'User') : [];
      console.log('🔍 DEBUGGING: Filtered user messages:', userMessages);
      
      if (userMessages.length === 0) {
        console.log('🎯 SKILLS STRATEGY: No user messages found - this is topic initialization, using existing requirements');
        // Use existing requirements for topic initialization
        aiMatchesResult = {
          search_gen_response: {
            icp_requirements: existingReqs
          }
        };
      } else {
        // Get the latest user message for LLM augmentation
        const latestUserMessage = userMessages.pop();
        let expandedUserInput = null;
        
        if (latestUserMessage && latestUserMessage.content) {
        console.log('🤖 SKILLS STRATEGY: Augmenting user message with LLM expansion...');
        
        try {
          // Create augmentation prompt
          const userInput = latestUserMessage.content;
          const agentInput = `Expand this candidate requirement to include domain expertise, seniority, experience, and related skills. Be fast and concise - keep response under 100 words. Clear text only, no formatting.`;
          
          // Call LLM API to augment the user message
          const apiUrl = decision.api_url || API_CONFIG.url;
          const { fetchChatCompletion } = await import('../api');
          const llmResponse = await fetchChatCompletion(apiUrl, userInput, agentInput);

          if (llmResponse && typeof llmResponse === 'string' && llmResponse.trim().length > 0) {
            console.log('🤖 SKILLS STRATEGY: LLM expansion received:', llmResponse.substring(0, 200) + '...');
            expandedUserInput = llmResponse.trim();
            console.log('🤖 SKILLS STRATEGY: Using expanded user input for runAIMatches');
          } else {
            console.log('🤖 SKILLS STRATEGY: No valid LLM expansion received, using original messages');
          }
        } catch (error) {
          console.error('🤖 SKILLS STRATEGY: Error augmenting user message:', error);
          console.log('🤖 SKILLS STRATEGY: Falling back to original messages');
        }
      }
      
        // Run AI matches with expanded user input or original messages
        if (expandedUserInput) {
          // Create new messages array with only the expanded user input
          const expandedMessages = [{
            role: 'User',
            content: expandedUserInput
          }];
          aiMatchesResult = await runAIMatches(expandedMessages);
        } else {
          // Use original messages if expansion failed
          aiMatchesResult = await runAIMatches(messages);
        }
        
        // 🎯 NEW: Handle unclear inputs by checking if AI matches produced meaningful results
        if (!aiMatchesResult) {
          throw new Error('Failed to get AI matches');
        }
        
        // 🎯 NEW: Handle unclear inputs using shared utility with copilot-powered questions
        console.log('🔍 SKILLS STRATEGY: About to call handleUnclearInput');
        const unclearInputResult = await handleUnclearInput(aiMatchesResult, 'skills', latestUserMessage, setMessages, decision);
        console.log('🔍 SKILLS STRATEGY: handleUnclearInput result:', unclearInputResult);
        if (unclearInputResult) {
          console.log('🔍 SKILLS STRATEGY: Returning unclear input result with clarifying question');
          return {
            ...unclearInputResult,
            strategyPhase: StrategyPhase.CONVERSATION
          };
        }
      }
    }
    
    // Step 2: Get requirements and queries (already extracted above for validation)
    const icpRequirements = aiMatchesResult.search_gen_response?.icp_requirements || [];
    const queries = decision.queries || {}; // Get queries from decision object
    
    const skillsRequirements = icpRequirements.filter(req => 
      req.section_name === "Must Have Skills" || 
      req.section_name === "Nice to Have Skills"
    );
    
    // Step 3: Push ALL requirements to central discovery queue - go through each requirement item by item
    if (icpRequirements.length > 0) {
      console.log('🎯 SKILLS STRATEGY: Processing ALL ICP requirements item by item:', icpRequirements.length, 'total requirements');
      
      // Ensure central discovery queue is initialized with session ID
      const sessionId = decision.sessionId || decision.currentSessionId;
      console.log('🎯 SKILLS STRATEGY: Session ID check:', {
        sessionId,
        decisionSessionId: decision.sessionId,
        decisionCurrentSessionId: decision.currentSessionId,
        hasSessionId: !!sessionId
      });

      if (sessionId) {
        console.log('🎯 SKILLS STRATEGY: Initializing enhanced central discovery queue with session ID:', sessionId);
        
        // Initialize the enhanced central discovery queue
        await centralDiscoveryQueue.initialize(sessionId, []);
        
        // Step 1: Check for "Must Have Skills" and generate LLM suggestions
        const mustHaveSkillsReq = icpRequirements.find(req => req.section_name === "Must Have Skills");
        const existingNiceToHaveReq = icpRequirements.find(req => req.section_name === "Nice to Have Skills");
        let skillSuggestions = [];
        
        if (mustHaveSkillsReq && mustHaveSkillsReq.bool_str && !existingNiceToHaveReq) {
          console.log('🤖 SKILLS STRATEGY: Found "Must Have Skills" and no existing "Nice to Have Skills", generating LLM suggestions...');
          
          try {
            // Extract skills from bool_str for LLM prompt
            const extractedSkills = extractSkillsFromRequirements([mustHaveSkillsReq], queries);
            const skillsText = extractedSkills.join(', ');
            
            console.log('🤖 SKILLS STRATEGY: Extracted skills for LLM:', skillsText);
            
            // Create LLM prompt
            const userInput = `Based on these required skills: ${skillsText}`;
            const agentInput = `Based on these required skills, suggest 3-5 complementary "nice to have" skills. Be fast and concise - respond with ONLY a comma-separated list of skill names under 100 words total.`;
            
            // Call LLM API
            const apiUrl = decision.api_url || API_CONFIG.url;
            const llmResponse = await import('../api').then(module => 
              module.fetchChatCompletion(apiUrl, userInput, agentInput)
            );
            
            if (llmResponse) {
              // Parse LLM response into individual skills
              skillSuggestions = llmResponse
                .split(',')
                .map(skill => skill.trim())
                .filter(skill => skill.length > 0)
                .slice(0, 5); // Limit to 5 suggestions
              
              console.log('🤖 SKILLS STRATEGY: LLM suggested skills:', skillSuggestions);
              
              // Create a "Nice to Have Skills" requirement with suggestions
              if (skillSuggestions.length > 0) {
                const suggestedBoolStr = `((${skillSuggestions.join(' OR ')}))`;
                const suggestionRequirement = {
                  section_id: `skills_gh`,
                  section_name: "Nice to Have Skills",
                  section_type: "Nice to Have",
                  section_order: 999, // Put it at the end
                  bool_str: suggestedBoolStr,
                  ai_generated: true, // Mark as AI-generated
                  ai_message: `✨ Based on your required skills, I suggest considering these complementary skills: ${skillSuggestions.join(', ')}`
                };
                
                // Add the suggestion requirement to the list
                icpRequirements.push(suggestionRequirement);
                console.log('✨ SKILLS STRATEGY: Added AI-suggested "Nice to Have Skills" requirement');
              }
            }
          } catch (error) {
            console.error('🤖 SKILLS STRATEGY: Error generating skill suggestions:', error);
          }
        } else if (existingNiceToHaveReq) {
          console.log('🤖 SKILLS STRATEGY: "Nice to Have Skills" already exists, skipping LLM suggestions to avoid duplicates');
        } else {
          console.log('🤖 SKILLS STRATEGY: No "Must Have Skills" found or empty bool_str, skipping LLM suggestions');
        }

        // Step 2: Check for existing queue items to prevent duplicates
        let existingQueueItems = centralDiscoveryQueue.getItemsByTopic('skills');
        console.log('🔍 SKILLS STRATEGY: Found existing skills queue items:', existingQueueItems.length);
        
        // Step 3: Go through each ICP requirement item by item and create queue items
        for (let i = 0; i < icpRequirements.length; i++) {
          const requirement = icpRequirements[i];
          if (requirement.section_id.indexOf("internal_ats_type") >= 0) {
            continue;
          }
          
          // Re-check queue items each iteration (in case we've added items in this loop)
          existingQueueItems = centralDiscoveryQueue.getItemsByTopic('skills');
          
          // Special handling for "Nice to Have Skills" - only allow one
          if (requirement.section_name === "Nice to Have Skills") {
            const existingNiceToHaveItems = existingQueueItems.filter(item => 
              item.section_name === "Nice to Have Skills"
            );
            
            if (existingNiceToHaveItems.length > 0) {
              console.log(`🔄 SKILLS STRATEGY: Found ${existingNiceToHaveItems.length} existing "Nice to Have Skills" items, updating the first one and removing others`);
              
              // Update the first one with latest data
              const primaryItem = existingNiceToHaveItems[0];
              // Get role type configuration for this specific requirement
              let localRoleTypeConfig = null;
              try {
                const { fetchRoleTypes } = await import('../api');
                const roleTypesData = await fetchRoleTypes();
                const currentRoleType = decision.updatedDiscoveryState?.roleType;
                
                if (currentRoleType && roleTypesData && roleTypesData.roleTypes) {
                  const roleTypeInfo = roleTypesData.roleTypes.find(rt => rt.id === currentRoleType);
                  if (roleTypeInfo) {
                    localRoleTypeConfig = {
                      ...roleTypeInfo,
                      advancedConfig: roleTypesData.advancedConfig
                    };
                  }
                }
              } catch (error) {
                console.error('❌ Error loading role types configuration:', error);
              }
              
              const updatedQueueItem = await createQueueItemFromRequirement(requirement, queries, localRoleTypeConfig);
              if (updatedQueueItem) {
                updatedQueueItem.queue_id = primaryItem.queue_id;
                updatedQueueItem.created_at = primaryItem.created_at;
                updatedQueueItem.updated_at = Date.now();
                
                if (requirement.ai_generated && requirement.ai_message) {
                  updatedQueueItem.ai_intro_message = requirement.ai_message;
                }
                
                centralDiscoveryQueue.queue.set(primaryItem.queue_id, updatedQueueItem);
                console.log(`✅ Updated primary "Nice to Have Skills" item`);
              }
              
              // Remove any additional "Nice to Have Skills" items
              for (let j = 1; j < existingNiceToHaveItems.length; j++) {
                centralDiscoveryQueue.queue.delete(existingNiceToHaveItems[j].queue_id);
                console.log(`🗑️ Removed duplicate "Nice to Have Skills" item:`, existingNiceToHaveItems[j].queue_id);
              }
              
              centralDiscoveryQueue.notifyListeners();
              continue;
            }
          }
          
          // General check for other requirement types
          const existingItem = existingQueueItems.find(item => 
            item.section_name === requirement.section_name && 
            item.section_type === requirement.section_type
          );
          
          if (existingItem) {
            console.log(`🔄 SKILLS STRATEGY: Updating existing queue item for "${requirement.section_name}" (${requirement.section_type}) with latest data`);
            
            // Update existing item with latest data
            // Get role type configuration for this specific requirement
            let localRoleTypeConfig = null;
            try {
              const { fetchRoleTypes } = await import('../api');
              const roleTypesData = await fetchRoleTypes();
              const currentRoleType = decision.updatedDiscoveryState?.roleType;
              
              if (currentRoleType && roleTypesData && roleTypesData.roleTypes) {
                const roleTypeInfo = roleTypesData.roleTypes.find(rt => rt.id === currentRoleType);
                if (roleTypeInfo) {
                  localRoleTypeConfig = {
                    ...roleTypeInfo,
                    advancedConfig: roleTypesData.advancedConfig
                  };
                }
              }
            } catch (error) {
              console.error('❌ Error loading role types configuration:', error);
            }
            
            const updatedQueueItem = await createQueueItemFromRequirement(requirement, queries, localRoleTypeConfig);
            if (updatedQueueItem) {
              // Preserve original queue_id and timestamps, but update content
              updatedQueueItem.queue_id = existingItem.queue_id;
              updatedQueueItem.created_at = existingItem.created_at;
              updatedQueueItem.updated_at = Date.now();
              
              // Add AI message if it's AI-generated
              if (requirement.ai_generated && requirement.ai_message) {
                updatedQueueItem.ai_intro_message = requirement.ai_message;
              }
              
              // Override the existing item in central queue
              centralDiscoveryQueue.queue.set(existingItem.queue_id, updatedQueueItem);
              centralDiscoveryQueue.notifyListeners();
              
              console.log(`✅ Updated existing queue item for: ${requirement.section_name}${requirement.ai_generated ? ' (AI-suggested)' : ''}`);
            }
            continue;
          }
          
          console.log(`🔍 Processing requirement ${i + 1}/${icpRequirements.length}:`, {
            section_name: requirement.section_name,
            section_type: requirement.section_type,
            bool_str: requirement.bool_str ? requirement.bool_str.substring(0, 100) + '...' : 'empty',
            ai_generated: requirement.ai_generated || false
          });
          
          try {
            // Get role type configuration for this specific requirement
            let localRoleTypeConfig = null;
            try {
              const { fetchRoleTypes } = await import('../api');
              const roleTypesData = await fetchRoleTypes();
              const currentRoleType = decision.updatedDiscoveryState?.roleType;
              
              if (currentRoleType && roleTypesData && roleTypesData.roleTypes) {
                const roleTypeInfo = roleTypesData.roleTypes.find(rt => rt.id === currentRoleType);
                if (roleTypeInfo) {
                  localRoleTypeConfig = {
                    ...roleTypeInfo,
                    advancedConfig: roleTypesData.advancedConfig
                  };
                }
              }
            } catch (error) {
              console.error('❌ Error loading role types configuration:', error);
            }
            
            const queueItem = await createQueueItemFromRequirement(requirement, queries, localRoleTypeConfig);
            if (queueItem) {
              // Add AI message to queue item if it's AI-generated
              if (requirement.ai_generated && requirement.ai_message) {
                queueItem.ai_intro_message = requirement.ai_message;
              }
              
              await centralDiscoveryQueue.addQueueItem(queueItem);
              console.log(`✅ Added queue item for: ${requirement.section_name}${requirement.ai_generated ? ' (AI-suggested)' : ''}`);
            } else {
              console.log(`⚠️ No queue item created for: ${requirement.section_name}`);
            }
          } catch (error) {
            console.error(`❌ Error creating queue item for ${requirement.section_name}:`, error);
          }
        }

        // Step 3.1: Add taxonomy-based skill queue items from Findem Magic queries
        try {
          // Debug: Check what's available in the decision object
          console.log('🔍 Decision object keys:', Object.keys(decision));
          console.log('🔍 Decision.roleType:', decision.roleType);
          console.log('🔍 Decision.updatedDiscoveryState:', decision.updatedDiscoveryState);
          console.log('🔍 Decision.updatedDiscoveryState.roleType:', decision.updatedDiscoveryState?.roleType);
          console.log('🔍 Decision.roleTypeConfig:', decision.roleTypeConfig);
          
          // Get role type configuration from API
          let roleTypeConfig = null;
          try {
            const { fetchRoleTypes } = await import('../api');
            const roleTypesData = await fetchRoleTypes();
            const roleType = decision.updatedDiscoveryState?.roleType || decision.roleType;
            
            console.log('🔍 Looking for roleType:', roleType);
            console.log('🔍 Available roleTypes:', roleTypesData?.roleTypes?.map(rt => rt.id));
            
            if (roleType && roleTypesData?.roleTypes) {
              roleTypeConfig = roleTypesData.roleTypes.find(rt => rt.id === roleType);
              if (roleTypeConfig && roleTypesData.advancedConfig) {
                roleTypeConfig.advancedConfig = roleTypesData.advancedConfig;
              }
              console.log('🔍 Found roleTypeConfig:', roleTypeConfig?.id);
            }
          } catch (error) {
            console.error('🚨 Error loading role types:', error);
          }
          
          console.log('🎯 Using role type config for taxonomy items:', roleTypeConfig?.id);
          const taxonomyQueueItems = createSkillTaxonomyQueueItems(queries, roleTypeConfig);
          for (const queueItem of taxonomyQueueItems) {
            await centralDiscoveryQueue.addQueueItem(queueItem);
            console.log(`✅ Added taxonomy-based queue item: ${queueItem.section_name}`);
          }
          console.log(`🎯 Added ${taxonomyQueueItems.length} taxonomy-based skill queue items`);
        } catch (error) {
          console.error('❌ Error creating taxonomy-based queue items:', error);
        }
        
        console.log('🎯 SKILLS STRATEGY: Enhanced central discovery queue populated with', centralDiscoveryQueue.getAllItems().length, 'items');
      } else {
        console.log('❌ SKILLS STRATEGY: No session ID available, cannot populate queue');
      }
    }
    
    // Step 4: Update legacy centralized requirements for backward compatibility
    if (skillsRequirements.length > 0 && decision.updateRequirements) {
      decision.updateRequirements(skillsRequirements, 'skills');
    }
    
    // Also update Findem Magic requirements if they exist
    const findemMagicReqs = icpRequirements.filter(req => req.section_name === "Findem Magic");
    if (findemMagicReqs.length > 0 && decision.updateRequirements) {
      decision.updateRequirements(findemMagicReqs, 'findem_magic');
    }
    
    // Step 4: Extract base skills for confirmation widget
    const baseSkills = extractSkillsFromRequirements(skillsRequirements, queries);
    
    // Step 5: Extract findem magic attributes and add to skills
    const expandedSkills = extractFindemMagicAttributes(icpRequirements, queries, baseSkills);
    
    // Step 6: No need to refresh results here - only refresh when user confirms items
    
    if (expandedSkills.length > 0) {
      // Step 7: Show skills confirmation widget with expanded skills
      console.log('Showing skills confirmation widget with expanded skills:', expandedSkills);
      
      // Remove "Typing..." message
      if (setMessages) {
        setMessages(prev => prev.filter(msg => msg.content !== 'Typing...'));
      }
      
      return {
        ...decision,
        actionType: 'RENDER_SKILLS_CONFIRMATION',
        componentProps: {
          skills: expandedSkills,
          setMessages,
          advanceToNextTopic: decision.advanceToNextTopic,
          onSendText: decision.onSendText,
          messages,
          onSkillsConfirmed: (confirmedSkills) => {
            console.log('Skills confirmed:', confirmedSkills);
            
            // Update the confirmed skills state
            if (decision.setConfirmedSkills) {
              decision.setConfirmedSkills(confirmedSkills);
            }
            
            // Update the requirements with selected skills using OR logic
            if (confirmedSkills.length > 0 && decision.updateRequirements) {
              const newBoolStr = `((${confirmedSkills.map(skill => `'${skill}'`).join(' OR ')}))`;
              const updatedRequirement = {
                section_id: "skills_mh",
                section_name: "Must Have Skills",
                section_type: "Must Have",
                section_order: 1,
                bool_str: newBoolStr
              };
              
              console.log('🔄 SKILLS STRATEGY: Updating requirements with selected skills:', {
                confirmedSkills,
                newBoolStr
              });
              
              decision.updateRequirements([updatedRequirement], 'skills');
            }
            
            // Add readonly skills confirmation component to messages
            if (setMessages) {
              setMessages(prev => [...prev, {
                role: 'AI',
                content: '', // Empty content since the component will render its own content
                component: 'SkillsConfirmation',
                componentProps: {
                  skills: confirmedSkills,
                  readOnly: true,
                  onConfirmed: null,
                  onAddMore: null
                },
                id: `skills_confirmed_readonly_${Date.now()}`,
                timestamp: new Date().toISOString(),
                _hasComponent: true,
                isReadOnly: true
              }]);
            }
            
            // Add confirmation message
            if (setMessages) {
              setTimeout(() => {
                setMessages(prev => [...prev, {
                  role: 'AI',
                  content: 'Perfect! I\'ve noted these skills for your search. Let\'s continue with the next topic.',
                  id: `ai_skills_confirmed_${Date.now()}`
                }]);
              }, 100);
            }
            
            // Advance to next topic
            if (decision.advanceToNextTopic) {
              setTimeout(() => {
                decision.advanceToNextTopic();
              }, 200);
            }
          },
          onAddMoreSkills: () => {
            console.log('User wants to add more skills');
            
            // Add message asking for more skills
            if (setMessages) {
              setMessages(prev => [...prev, {
                role: 'AI',
                content: 'Great! Please continue chatting below to add more skills to your requirements.',
                id: `ai_skills_add_more_${Date.now()}`
              }]);
            }
          }
        },
        disableFooter: true, // Disable footer when showing confirmation widget
        strategyPhase: StrategyPhase.CONVERSATION
      };
    } else {
      // No skills found, continue with regular conversation
      return {
        ...decision,
        actionType: 'SEND_TO_BACKEND',
        agentGuidance: 'I\'d like to understand what skills are important for this role. Could you tell me about the key technical and soft skills you\'re looking for?',
        strategyPhase: StrategyPhase.CONVERSATION
      };
    }
    
  } catch (error) {
    console.error('Error in skills strategy:', error);
    
    // Fall back to regular conversation
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: 'Let\'s discuss the skills requirements for this role. What technical and soft skills are you looking for?',
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

export default skillsStrategy;
